import type { FrameShapeId } from "@/content/guides/types";

/**
 * Illustration d'une forme de monture — deux verres + pont + amorces de
 * branches, dessinés en SVG. Aucun visuel photographique : le rendu reste
 * identique d'une page à l'autre et pèse quelques centaines d'octets.
 */

interface Geometry {
  /** Tracé d'un verre gauche (le droit est obtenu par symétrie). */
  lens: string;
  /** Tracé du pont. */
  bridge: string;
  /** Barre supérieure optionnelle (browline). */
  brow?: string;
}

const G: Record<FrameShapeId, Geometry> = {
  round: {
    lens: "M34 30a18 18 0 1 1 0 .1Z",
    bridge: "M52 30q8 -6 16 0",
  },
  rectangle: {
    lens: "M16 20h36a4 4 0 0 1 4 4v14a4 4 0 0 1-4 4H16a4 4 0 0 1-4-4V24a4 4 0 0 1 4-4Z",
    bridge: "M56 28q4 -4 8 0",
  },
  square: {
    lens: "M16 16h36a4 4 0 0 1 4 4v22a4 4 0 0 1-4 4H16a4 4 0 0 1-4-4V20a4 4 0 0 1 4-4Z",
    bridge: "M56 26q4 -4 8 0",
  },
  aviator: {
    lens: "M12 20h44c0 0-2 26-14 26-11 0-13-9-17-15-3-5-8-8-13-11Z",
    bridge: "M56 21q4 -5 8 0",
  },
  cateye: {
    lens: "M12 26c6-10 30-14 44-8 0 14-8 24-22 24-13 0-24-6-22-16Z",
    bridge: "M56 20q4 -4 8 0",
  },
  oval: {
    lens: "M34 16c12 0 22 6 22 15s-10 15-22 15-22-6-22-15 10-15 22-15Z",
    bridge: "M56 29q4 -4 8 0",
  },
  browline: {
    lens: "M14 26h40v10c0 7-8 12-20 12s-20-5-20-12Z",
    bridge: "M54 26h10",
    brow: "M10 22h48a3 3 0 0 0 0-6H12a3 3 0 0 0-2 6Z",
  },
  geometric: {
    lens: "M14 18h38l4 12-8 16H20l-8-16Z",
    bridge: "M56 27q4 -4 8 0",
  },
};

interface Props {
  shape: FrameShapeId;
  title?: string | undefined;
  className?: string;
  size?: number;
}

export function FrameShapeIllustration({ shape, title, className, size = 120 }: Props) {
  const g = G[shape];
  const decorative = !title;

  return (
    <svg
      viewBox="0 0 120 60"
      width={size}
      height={size / 2}
      className={className}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      {...(title ? { "aria-label": title } : {})}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <g
        fill="currentColor"
        fillOpacity={0.08}
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      >
        <path d={g.lens} />
        <g transform="translate(120,0) scale(-1,1)">
          <path d={g.lens} />
        </g>
        <path d={g.bridge} fill="none" />
        {g.brow ? (
          <>
            <path d={g.brow} fillOpacity={0.25} />
            <g transform="translate(120,0) scale(-1,1)">
              <path d={g.brow} fillOpacity={0.25} />
            </g>
          </>
        ) : null}
        {/* amorces de branches */}
        <path d="M8 24 2 27" fill="none" />
        <path d="M112 24 118 27" fill="none" />
      </g>
    </svg>
  );
}
